import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Archive, ArchiveRestore, Calendar, Inbox, Info, Loader2, MapPin, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { useSignedPhotoUrl } from "@/lib/photoUrl";
import { anonymizeName } from "@/utils/anonymization";

function RequestAvatar({
  url,
  alt,
  fallback,
}: {
  url?: string | null;
  alt?: string;
  fallback?: string;
}) {
  const signed = useSignedPhotoUrl(url ?? undefined);
  return (
    <Avatar className="h-16 w-16">
      <AvatarImage src={signed ?? undefined} alt={alt} />
      <AvatarFallback className="text-lg">{fallback ?? "?"}</AvatarFallback>
    </Avatar>
  );
}

interface PendingRequest {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  profile_picture_url: string | null;
  created_at: string;
  archived_at: string | null;
}

type View = "pending" | "archived";

export default function MatchmakerJoinRequests() {
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get("view") === "archived" ? "archived" : "pending";

  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        // Show only self-submitted profiles that haven't been actioned yet.
        // `status` stays "pending" after approve (only approved_at/approved_by
        // are set), so we filter on approved_at IS NULL to hide already-approved
        // rows from the pending list once the user navigates back.
        // Archived requests (archived_at set) live in their own tab.
        let query = supabase
          .from("profiles")
          .select("id, first_name, age, gender, location, profile_picture_url, created_at, archived_at")
          .eq("status", "pending")
          .eq("submission_source", "self")
          .is("approved_at", null);

        query =
          view === "archived"
            ? query.not("archived_at", "is", null).order("archived_at", { ascending: false })
            : query.is("archived_at", null).order("created_at", { ascending: false });

        const { data, error: queryError } = await query;

        if (queryError) throw queryError;
        if (!cancelled) setRequests((data ?? []) as PendingRequest[]);
      } catch (err: any) {
        console.error("join_requests.load_failed");
        if (!cancelled) {
          setError(err.message || "Failed to load join requests.");
          toast({
            title: "Error",
            description: err.message || "Failed to load join requests.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRequests();
    return () => {
      cancelled = true;
    };
  }, [view]);

  const setArchived = async (id: string, archived: boolean) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const { error: rpcError } = await supabase.rpc("set_join_request_archived", {
        _profile_id: id,
        _archived: archived,
      });
      if (rpcError) throw rpcError;
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast({
        title: archived ? "Request archived" : "Request restored",
        description: archived
          ? "Moved to Archived. The applicant is not notified."
          : "Moved back to Pending.",
      });
    } catch (err: any) {
      console.error(archived ? "join_request.archive_failed" : "join_request.unarchive_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to update request.",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const detailBasePath = location.pathname.startsWith("/admin")
    ? "/admin/join-requests"
    : "/matchmaker/join-requests";

  return (
    <Layout title="Join Requests">
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border border-primary/20 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            {view === "archived" ? (
              <Archive className="h-7 w-7 text-primary" />
            ) : (
              <Inbox className="h-7 w-7 text-primary" />
            )}
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {view === "archived" ? "Archived Join Requests" : "Pending Join Requests"}
            </h2>
            {!loading && (
              <Badge variant="secondary" className="ml-2">
                {requests.length}
              </Badge>
            )}
          </div>
          <p className="text-foreground/70">
            {view === "archived"
              ? "Requests set aside without a decision. Applicants are not notified. Restore one to move it back to Pending."
              : "Review self-submitted profiles and approve, decline, or archive them."}
          </p>
        </div>

        <Tabs
          value={view}
          onValueChange={(v) => {
            const next = new URLSearchParams(searchParams);
            if (v === "archived") next.set("view", "archived");
            else next.delete("view");
            setSearchParams(next, { replace: true });
          }}
          className="mb-6"
        >
          <TabsList>
            <TabsTrigger value="pending">
              <Inbox className="mr-2 h-4 w-4" />
              Pending
            </TabsTrigger>
            <TabsTrigger value="archived">
              <Archive className="mr-2 h-4 w-4" />
              Archived
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <Alert>
            {view === "archived" ? <Archive className="h-4 w-4" /> : <Inbox className="h-4 w-4" />}
            <AlertTitle>{view === "archived" ? "Nothing archived" : "All caught up"}</AlertTitle>
            <AlertDescription>
              {view === "archived"
                ? "No archived requests."
                : "No pending requests right now."}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => (
              <Card key={req.id} className="hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <RequestAvatar
                        url={req.profile_picture_url}
                        alt={req.first_name}
                        fallback={isAdmin ? "?" : req.first_name?.[0]?.toUpperCase()}
                      />

                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg">
                            {isAdmin ? anonymizeName(req.first_name) : req.first_name}
                            {req.age ? `, ${req.age}` : ""}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            Self-submitted
                          </Badge>
                          {req.archived_at && (
                            <Badge variant="secondary" className="text-xs">
                              Archived
                            </Badge>
                          )}
                        </div>

                        <div className="grid gap-1.5 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="h-4 w-4" />
                            <span className="capitalize">
                              {req.gender === "male"
                                ? "Brother"
                                : req.gender === "female"
                                  ? "Sister"
                                  : req.gender}
                            </span>
                          </div>
                          {req.location && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              <span>{req.location}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>
                              Submitted{" "}
                              {formatDistanceToNow(new Date(req.created_at), {
                                addSuffix: true,
                              })}
                              {req.archived_at && (
                                <>
                                  {" · Archived "}
                                  {formatDistanceToNow(new Date(req.archived_at), {
                                    addSuffix: true,
                                  })}
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2">
                      <Button
                        onClick={() => navigate(`${detailBasePath}/${req.id}`)}
                      >
                        Review
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setArchived(req.id, view !== "archived")}
                        disabled={busyId !== null}
                      >
                        {busyId === req.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : view === "archived" ? (
                          <ArchiveRestore className="mr-2 h-4 w-4" />
                        ) : (
                          <Archive className="mr-2 h-4 w-4" />
                        )}
                        {view === "archived" ? "Restore" : "Archive"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
